import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail, Phone, Facebook, Instagram, User, X } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/staff`)
      .then(r => setStaff(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="pt-32 pb-24 bg-white">
      {/* Bright top section */}
      <section className="bg-[#f5f2ec] pt-16 pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5b6c72] hover:text-[#0e5469] mb-10">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-14 items-start">
            <div>
              <div className="text-[#0e5469] uppercase tracking-[0.32em] text-xs font-bold mb-6">Team Work</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#0a2d38] leading-[1.02] tracking-tight">
                Workers Together<br />
                <span className="text-[#c8a36a]">For God&rsquo;s Glory</span>
              </h1>
            </div>
            <div className="lg:pt-8">
              <p className="text-lg text-[#3b4d54] leading-relaxed">
                This church functions because we work as a team. We&rsquo;re giving God our best, and we believe the best days of First Assembly Living Waters are still ahead.
              </p>
              <p className="mt-5 text-lg text-[#3b4d54] leading-relaxed">
                We&rsquo;d love to meet you and welcome you into our family.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Staff carousel */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 -mt-16 relative z-10">
        {loading ? (
          <div className="text-center py-20 text-[#7a8a8f]">Loading&hellip;</div>
        ) : staff.length === 0 ? (
          <div className="text-center py-20 text-[#7a8a8f] bg-white rounded-md">
            Staff bios coming soon. Contact us anytime at{" "}
            <a href="mailto:pastor@firstassemblylivingwaters.org" className="text-[#0e5469] underline">
              pastor@firstassemblylivingwaters.org
            </a>.
          </div>
        ) : (
          <Carousel opts={{ align: "start", loop: staff.length > 2 }} className="w-full">
            <CarouselContent className="-ml-4">
              {staff.map((s) => (
                <CarouselItem key={s.id} className="pl-4 basis-[85%] sm:basis-[60%] md:basis-1/2 lg:basis-1/3">
                  <button
                    type="button"
                    onClick={() => setActive(s)}
                    className="group relative w-full aspect-[3/4] overflow-hidden rounded-md bg-[#f5f2ec] text-left shadow-[0_20px_50px_-30px_rgba(14,84,105,0.35)]"
                  >
                    {s.photo ? (
                      <img src={s.photo} alt={s.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-[#e7dfd0] to-[#f5f2ec]">
                        <div className="h-28 w-28 rounded-full bg-[#0e5469] flex items-center justify-center">
                          <User className="h-12 w-12 text-[#c8a36a]" />
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      {s.role && <div className="text-xs md:text-sm opacity-95 mb-1.5">{s.role}</div>}
                      <div className="text-2xl md:text-3xl font-bold leading-tight">{s.name}</div>
                    </div>
                    <div className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white text-[#0a2d38] flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="h-5 w-5" />
                    </div>
                    <div className="absolute bottom-5 right-5 text-[11px] uppercase tracking-[0.2em] text-white/85 font-semibold flex items-center gap-1.5">
                      See more info <ArrowRight className="h-3 w-3" />
                    </div>
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-center gap-3 mt-8">
              <CarouselPrevious className="static translate-y-0 h-12 w-12 bg-white border-[#e7dfd0] text-[#0a2d38] hover:bg-[#c8a36a] hover:text-white hover:border-[#c8a36a]" />
              <CarouselNext className="static translate-y-0 h-12 w-12 bg-white border-[#e7dfd0] text-[#0a2d38] hover:bg-[#c8a36a] hover:text-white hover:border-[#c8a36a]" />
            </div>
          </Carousel>
        )}
      </section>

      {/* Contact CTA */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 mt-24 text-center">
        <div className="p-10 md:p-14 bg-[#f5f2ec] rounded-md">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0a2d38]">Have questions?</h2>
          <p className="mt-4 text-[#3b4d54] text-lg">We&rsquo;d love to talk &mdash; even just to say hi.</p>
          <a href="mailto:pastor@firstassemblylivingwaters.org" className="mt-7 inline-flex items-center gap-2 bg-[#c8a36a] hover:bg-[#b48d52] text-[#0a2d38] rounded-full px-8 py-3.5 font-bold transition-colors">
            <Mail className="h-4 w-4" /> Contact Us
          </a>
        </div>
      </section>

      {/* Staff detail modal */}
      {active && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="relative w-full max-w-3xl bg-white rounded-md overflow-hidden shadow-2xl grid md:grid-cols-[45%_1fr]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setActive(null)} className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/95 text-[#0a2d38] hover:bg-[#c8a36a] hover:text-white flex items-center justify-center shadow z-10">
              <X className="h-5 w-5" />
            </button>
            <div className="aspect-square md:aspect-auto bg-[#f5f2ec] relative">
              {active.photo ? (
                <img src={active.photo} alt={active.name} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <div className="h-24 w-24 rounded-full bg-[#0e5469] flex items-center justify-center">
                    <User className="h-10 w-10 text-[#c8a36a]" />
                  </div>
                </div>
              )}
            </div>
            <div className="p-8 md:p-10">
              {active.role && <div className="text-[#0e5469] uppercase tracking-[0.24em] text-xs font-bold">{active.role}</div>}
              <h3 className="text-3xl md:text-4xl font-bold text-[#0a2d38] mt-2 leading-tight">{active.name}</h3>
              {active.bio && <p className="mt-5 text-[#3b4d54] leading-relaxed whitespace-pre-wrap">{active.bio}</p>}
              <div className="mt-7 pt-5 border-t border-[#e7dfd0] flex flex-wrap items-center gap-5">
                {active.email && <a href={`mailto:${active.email}`} className="inline-flex items-center gap-2 text-sm text-[#0e5469] hover:text-[#c8a36a] font-medium"><Mail className="h-4 w-4" /> Email</a>}
                {active.phone && <a href={`tel:${active.phone.replace(/\D/g, "")}`} className="inline-flex items-center gap-2 text-sm text-[#0e5469] hover:text-[#c8a36a] font-medium"><Phone className="h-4 w-4" /> {active.phone}</a>}
                {active.facebook && <a href={active.facebook} target="_blank" rel="noreferrer" className="text-[#0e5469] hover:text-[#c8a36a]"><Facebook className="h-4 w-4" /></a>}
                {active.instagram && <a href={active.instagram} target="_blank" rel="noreferrer" className="text-[#0e5469] hover:text-[#c8a36a]"><Instagram className="h-4 w-4" /></a>}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
